import { CALL_STATES } from "@/components/appointments/call/callStates.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

export const CALL_MEDIA_STATES = {
  UNKNOWN: "unknown",
  READY: "ready",
  MISSING: "missing",
  DENIED: "denied",
  UNAVAILABLE: "unavailable",
};

function cleanText(value = "") {
  const text = String(value || "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase();
  if (
    normalized === "[object object]" ||
    normalized === "object" ||
    normalized === "null" ||
    normalized === "undefined"
  ) {
    return "";
  }

  return text;
}

function firstText(...values) {
  for (const value of values) {
    const text = cleanText(value);
    if (text) return text;
  }
  return "";
}

function combineName(firstName = "", lastName = "") {
  return [cleanText(firstName), cleanText(lastName)].filter(Boolean).join(" ");
}

function pickAvatar(...candidates) {
  for (const candidate of candidates) {
    const text = cleanText(candidate);
    if (text) return text;
  }
  return "";
}

function resolveActorRole({ auth = null, routeState = null, snapshot = null } = {}) {
  const explicit = cleanText(routeState?.actorRole || snapshot?.actorRole).toLowerCase();
  if (explicit === "doctor" || explicit === "patient") return explicit;

  const profileRole = cleanText(auth?.profile?.role).toLowerCase();
  if (profileRole === "doctor" || profileRole === "patient") return profileRole;

  if (auth?.profile?.hasDoctor && !auth?.profile?.hasPatient) return "doctor";
  if (!auth?.profile?.hasDoctor && auth?.profile?.hasPatient) return "patient";
  return "patient";
}

function resolvePatientName(...candidates) {
  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string") {
      const text = cleanText(candidate);
      if (text && text !== "Patient") return text;
      continue;
    }

    const text = firstText(
      candidate?.patientName,
      candidate?.patient_name,
      candidate?.full_name,
      candidate?.fullName,
      candidate?.name,
      candidate?.display_name,
      candidate?.displayName,
      candidate?.profile?.display_name,
      candidate?.patient?.full_name,
      candidate?.patient?.name,
      candidate?.patient?.display_name,
      candidate?.patientProfile?.full_name,
      candidate?.patientProfile?.name,
      candidate?.patientProfile?.display_name,
      combineName(candidate?.first_name, candidate?.last_name),
      combineName(candidate?.firstName, candidate?.lastName)
    );

    if (text && text !== "Patient") return text;
  }

  return "Patient";
}

function resolvePatientAvatar(...candidates) {
  for (const candidate of candidates) {
    if (!candidate || typeof candidate === "string") continue;

    const avatar = pickAvatar(
      candidate?.avatar,
      candidate?.avatar_url,
      candidate?.profile_image_url,
      candidate?.profile_photo_url,
      candidate?.photo_url,
      candidate?.photo,
      candidate?.patient?.avatar,
      candidate?.patient?.avatar_url,
      candidate?.patient?.profile_image_url,
      candidate?.patient?.photo_url,
      candidate?.patientProfile?.avatar,
      candidate?.patientProfile?.avatar_url,
      candidate?.patientProfile?.profile_image_url,
      candidate?.authProfile?.avatar_url
    );

    if (avatar) return avatar;
  }

  return "/icons/circular-avatar-placeholder.svg";
}

function resolveDoctorAvatar(...candidates) {
  for (const candidate of candidates) {
    if (!candidate || typeof candidate === "string") continue;

    const avatar = pickAvatar(
      candidate?.photo_url,
      candidate?.avatar_url,
      candidate?.profile_photo_url,
      candidate?.profile_image_url,
      candidate?.avatar,
      candidate?.doctor?.photo_url,
      candidate?.doctor?.avatar_url,
      candidate?.doctor?.profile_photo_url,
      candidate?.doctor?.profile_image_url,
      candidate?.doctor?.avatar,
      candidate?.profile?.photo_url,
      candidate?.profile?.avatar_url
    );

    if (avatar) return avatar;
  }

  return "";
}

function resolveDoctorId(...candidates) {
  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string") {
      const text = cleanText(candidate);
      if (text) return text;
      continue;
    }

    const text = firstText(
      candidate?.doctor_user_id,
      candidate?.user_id,
      candidate?.id,
      candidate?.doctor?.doctor_user_id,
      candidate?.doctor?.user_id,
      candidate?.doctor?.id
    );
    if (text) return text;
  }
  return null;
}

function resolvePatientId(...candidates) {
  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string") {
      const text = cleanText(candidate);
      if (text) return text;
      continue;
    }

    const text = firstText(
      candidate?.patientUserId,
      candidate?.patient_user_id,
      candidate?.user_id,
      candidate?.patientId,
      candidate?.patient_id,
      candidate?.id,
      candidate?.patient?.user_id,
      candidate?.patient?.id,
      candidate?.patientProfile?.user_id,
      candidate?.patientProfile?.id
    );
    if (text) return text;
  }
  return null;
}

function normalizeMediaState(value, fallback = CALL_MEDIA_STATES.UNKNOWN) {
  const next = cleanText(value).toLowerCase();
  if (!next) return fallback;
  if (Object.values(CALL_MEDIA_STATES).includes(next)) return next;
  return fallback;
}

export function resolveConsultationParticipants({
  auth = null,
  routeState = null,
  snapshot = null,
} = {}) {
  const actorRole = resolveActorRole({ auth, routeState, snapshot });

  const patientName = resolvePatientName(
    routeState?.patientName,
    snapshot?.patientName,
    routeState?.patient,
    snapshot?.patient,
    snapshot?.patientProfile,
    actorRole === "patient" ? auth?.profile : null,
    routeState?.restored,
    snapshot?.restored
  );

  const patientAvatar = resolvePatientAvatar(
    actorRole === "doctor" && routeState?.counterpartAvatar
      ? { avatar: routeState.counterpartAvatar }
      : null,
    routeState?.patient,
    snapshot?.patient,
    snapshot?.patientProfile,
    actorRole === "patient" ? auth?.profile : null,
    routeState?.restored,
    snapshot?.restored
  );

  const doctorName = resolvePatientFacingDoctorName(
    routeState?.doctor,
    snapshot?.doctor,
    actorRole === "doctor" ? auth?.profile : null,
    actorRole === "patient" ? routeState?.counterpartName : "",
    actorRole === "patient" ? snapshot?.counterpartName : "",
    routeState,
    snapshot,
    routeState?.doctorName,
    snapshot?.doctorName
  );

  const doctorAvatar = resolveDoctorAvatar(
    actorRole === "patient" && routeState?.counterpartAvatar
      ? { photo_url: routeState.counterpartAvatar }
      : null,
    routeState?.doctor,
    snapshot?.doctor,
    actorRole === "doctor" ? auth?.profile : null,
    routeState?.restored,
    snapshot?.restored
  );

  const doctor = {
    role: "doctor",
    id: resolveDoctorId(routeState?.doctor, snapshot?.doctor, auth?.profile, routeState, snapshot),
    displayName: doctorName || "Doctor",
    avatar: doctorAvatar || "/icons/circular-avatar-placeholder.svg",
  };

  const patient = {
    role: "patient",
    id: resolvePatientId(
      routeState,
      snapshot,
      routeState?.patient,
      snapshot?.patient,
      snapshot?.patientProfile,
      actorRole === "patient" ? auth?.profile : null,
      actorRole === "patient" ? auth?.user?.id : null
    ),
    displayName: patientName || "Patient",
    avatar: patientAvatar || "/icons/circular-avatar-placeholder.svg",
  };

  return {
    actorRole,
    doctor,
    patient,
    local: actorRole === "doctor" ? doctor : patient,
    remote: actorRole === "doctor" ? patient : doctor,
  };
}

export function buildConsultationSummaryState({
  appointmentId,
  routeState = null,
  snapshot = null,
  participants = null,
} = {}) {
  const resolvedParticipants =
    participants || resolveConsultationParticipants({ routeState, snapshot });

  return {
    appointmentId: String(appointmentId || ""),
    doctorName: resolvedParticipants?.doctor?.displayName || "Doctor",
    endedAt: new Date().toISOString(),
    vitalRepStatus: "pending",
    actorRole:
      resolvedParticipants?.actorRole ||
      cleanText(routeState?.actorRole || snapshot?.actorRole).toLowerCase() ||
      "patient",
    counterpartName:
      resolvedParticipants?.remote?.displayName ||
      cleanText(routeState?.counterpartName || snapshot?.counterpartName) ||
      "Doctor",
    counterpartAvatar:
      cleanText(routeState?.counterpartAvatar || snapshot?.counterpartAvatar) ||
      resolvedParticipants?.remote?.avatar ||
      "",
    patientId:
      cleanText(routeState?.patientId || snapshot?.patientId || snapshot?.patient_id) ||
      resolvedParticipants?.patient?.id ||
      null,
    patient: routeState?.patient || snapshot?.patient || null,
    restored: routeState?.restored || snapshot || null,
  };
}

export function createConsultationTransport({
  kind = "audio",
  phase = CALL_STATES.IDLE,
  permissions = {},
  devices = {},
  media = {},
} = {}) {
  const connection =
    phase === CALL_STATES.RECONNECTING
      ? "reconnecting"
      : phase === CALL_STATES.CONNECTING || phase === CALL_STATES.RINGING
        ? "connecting"
        : phase === CALL_STATES.IN_CALL
          ? "connected"
          : phase === CALL_STATES.ENDED
            ? "ended"
            : "idle";

  const normalizedPermissions = {
    microphone: normalizeMediaState(permissions?.microphone),
    camera: normalizeMediaState(permissions?.camera),
  };

  const normalizedDevices = {
    microphone: normalizeMediaState(devices?.microphone),
    camera: normalizeMediaState(devices?.camera),
  };

  return {
    kind: cleanText(kind).toLowerCase() === "video" ? "video" : "audio",
    connection,
    permissions: normalizedPermissions,
    devices: normalizedDevices,
    media: {
      speakerOn: media?.speakerOn !== false,
      microphoneOn: media?.microphoneOn !== false,
      cameraOn: media?.cameraOn !== false,
    },
    hasPermissionIssue:
      normalizedPermissions.camera === CALL_MEDIA_STATES.DENIED ||
      normalizedPermissions.microphone === CALL_MEDIA_STATES.DENIED,
    hasDeviceIssue:
      normalizedDevices.camera === CALL_MEDIA_STATES.MISSING ||
      normalizedDevices.camera === CALL_MEDIA_STATES.UNAVAILABLE ||
      normalizedDevices.microphone === CALL_MEDIA_STATES.MISSING ||
      normalizedDevices.microphone === CALL_MEDIA_STATES.UNAVAILABLE,
  };
}
